/**
 * 현장 수령·반납 처리 (휴대폰 화면 기준)
 *  수량 → 부속품 체크리스트(기준과 다르면 빨간색 + 메모 필수) → 사진(필수, 최대 3장) → 대여자 이름 → 양쪽 서명
 */
import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, MenuItem, Paper, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { fmtLong, RentalItem, RentalSettings, rentalApi, Reservation } from '../api';
import { overdueDays } from '../logic';

type Props = { reservation: Reservation; type: 'pickup' | 'ret'; items: RentalItem[]; settings: RentalSettings; today: string; onDone: (msg: string) => void; onBack: () => void };

/** 사진은 긴 변 1280px JPEG 로 줄여서 올림 */
const shrink = (file: File) => new Promise<Blob>((resolve, reject) => {
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.onload = () => {
    const s = Math.min(1, 1280 / Math.max(img.width, img.height));
    const c = document.createElement('canvas');
    c.width = Math.round(img.width * s); c.height = Math.round(img.height * s);
    c.getContext('2d')!.drawImage(img, 0, 0, c.width, c.height);
    c.toBlob((b) => { URL.revokeObjectURL(url); b ? resolve(b) : reject(new Error('사진을 읽지 못했어요.')); }, 'image/jpeg', 0.8);
  };
  img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('사진을 읽지 못했어요. 다른 사진으로 다시 해 보세요.')); };
  img.src = url;
});

const SignaturePad: React.FC<{ label: string; onChange: (b: Blob | null) => void }> = ({ label, onChange }) => {
  const ref = useRef<HTMLCanvasElement>(null);
  const drawn = useRef(false);
  useEffect(() => {
    const c = ref.current!;
    const dpr = window.devicePixelRatio || 1;
    const w = c.clientWidth;
    c.width = w * dpr; c.height = 140 * dpr;
    const x = c.getContext('2d')!;
    x.scale(dpr, dpr); x.lineWidth = 2.4; x.lineCap = 'round'; x.lineJoin = 'round'; x.strokeStyle = '#111827';
    let down = false; let lx = 0; let ly = 0;
    const pos = (e: PointerEvent) => { const b = c.getBoundingClientRect(); return [e.clientX - b.left, e.clientY - b.top]; };
    const onDown = (e: PointerEvent) => { down = true; c.setPointerCapture(e.pointerId); [lx, ly] = pos(e); x.beginPath(); x.arc(lx, ly, 1.1, 0, 7); x.fillStyle = '#111827'; x.fill(); };
    const onMove = (e: PointerEvent) => { if (!down) return; const [nx, ny] = pos(e); x.beginPath(); x.moveTo(lx, ly); x.lineTo(nx, ny); x.stroke(); lx = nx; ly = ny; drawn.current = true; };
    const onUp = () => {
      if (!down) return; down = false; drawn.current = true;
      const out = document.createElement('canvas'); out.width = c.width; out.height = c.height;
      const o = out.getContext('2d')!; o.fillStyle = '#fff'; o.fillRect(0, 0, out.width, out.height); o.drawImage(c, 0, 0);
      out.toBlob((b) => onChange(b), 'image/png');
    };
    c.addEventListener('pointerdown', onDown); c.addEventListener('pointermove', onMove);
    c.addEventListener('pointerup', onUp); c.addEventListener('pointercancel', onUp);
    return () => { c.removeEventListener('pointerdown', onDown); c.removeEventListener('pointermove', onMove); c.removeEventListener('pointerup', onUp); c.removeEventListener('pointercancel', onUp); };
  }, [onChange]);
  const clear = () => { const c = ref.current!; c.getContext('2d')!.clearRect(0, 0, c.width, c.height); drawn.current = false; onChange(null); };
  return (
    <Box sx={{ position: 'relative', border: '1px dashed #94a3b8', borderRadius: 2, bgcolor: '#fff' }}>
      <Typography sx={{ position: 'absolute', left: 10, top: 6, fontSize: 12, color: 'text.secondary', pointerEvents: 'none' }}>{label}</Typography>
      <Button size="small" onClick={clear} sx={{ position: 'absolute', right: 4, top: 2 }}>다시 쓰기</Button>
      <canvas ref={ref} style={{ display: 'block', width: '100%', height: 140, touchAction: 'none' }} aria-label={label} />
    </Box>
  );
};

const Stepper: React.FC<{ value: number; base: number; onChange: (n: number) => void; label: string }> = ({ value, base, onChange, label }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
    <Button size="small" variant="outlined" sx={{ minWidth: 34, px: 0, fontSize: 18, lineHeight: 1 }} onClick={() => onChange(Math.max(0, value - 1))} aria-label={`${label} 줄이기`}>−</Button>
    <TextField size="small" value={value} onChange={(e) => onChange(Math.max(0, Number(e.target.value.replace(/\D/g, '')) || 0))}
      inputProps={{ inputMode: 'numeric', style: { textAlign: 'center', width: 44, fontWeight: value !== base ? 800 : 400, color: value !== base ? '#dc2626' : undefined } }} />
    <Button size="small" variant="outlined" sx={{ minWidth: 34, px: 0, fontSize: 18, lineHeight: 1 }} onClick={() => onChange(value + 1)} aria-label={`${label} 늘리기`}>+</Button>
  </Box>
);

const HandoverForm: React.FC<Props> = ({ reservation: r, type, items, settings, today, onDone, onBack }) => {
  const isP = type === 'pickup';
  const baseQty = (l: Reservation['lines'][number]) => (isP ? l.qty : (l.outQty ?? l.qty));
  const [qty, setQty] = useState<Record<string, number>>(() => Object.fromEntries(r.lines.map((l) => [l.itemId, baseQty(l)])));
  const [chk, setChk] = useState<Record<string, number | string>>({});
  const [cond, setCond] = useState('정상');
  const [note, setNote] = useState('');
  const [photos, setPhotos] = useState<Array<{ blob: Blob; url: string }>>([]);
  const [renter, setRenter] = useState(r.contact);
  const [renterPhone, setRenterPhone] = useState(r.phone);
  const [staff, setStaff] = useState(settings.staff?.[0] || '본부 담당자');
  const [sigA, setSigA] = useState<Blob | null>(null);
  const [sigB, setSigB] = useState<Blob | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const late = overdueDays(r, today);

  const checkRows = r.lines.flatMap((l) => {
    const it = items.find((x) => x._id === l.itemId);
    return (it?.checks || []).map((c, k) => {
      const key = `${l.itemId}#${k}`;
      const std = c.type === 'ok' ? 0 : (c.std || 0) * (qty[l.itemId] || 0);
      const value = chk[key] ?? (c.type === 'ok' ? '정상' : std);
      return { key, item: l.name, itemId: l.itemId, c, std, value };
    });
  });
  const qtyDiff = r.lines.some((l) => qty[l.itemId] !== baseQty(l));
  const checkDiff = checkRows.some((x) => (x.c.type === 'ok' ? x.value !== '정상' : x.value !== x.std));
  const needNote = qtyDiff || checkDiff || (!isP && cond !== '정상');

  const addPhoto = async (f?: File | null) => {
    if (!f) return;
    try { const blob = await shrink(f); setPhotos((p) => [...p, { blob, url: URL.createObjectURL(blob) }].slice(0, 3)); } catch (e: any) { setError(e.message); }
  };
  const save = async () => {
    const errs: string[] = [];
    if (!photos.length) errs.push('사진을 1장 이상 찍어 주세요.');
    if (!renter.trim()) errs.push(`${isP ? '대여자' : '반납자'} 이름을 입력하세요.`);
    if (!sigA) errs.push(`${isP ? '대여자' : '반납자'} 서명을 받아 주세요.`);
    if (!sigB) errs.push('본부 담당자 서명을 해 주세요.');
    if (needNote && !note.trim()) errs.push('기준과 다른 항목이 있어요. 메모를 남겨 주세요.');
    if (errs.length) { setError(errs.join(' ')); return; }
    const form = new FormData();
    form.append('data', JSON.stringify({
      lines: r.lines.map((l) => ({ itemId: l.itemId, qty: qty[l.itemId] || 0 })),
      checks: checkRows.map((x) => ({ item: x.item, label: x.c.label, type: x.c.type, std: x.std, unit: x.c.unit, value: x.value })),
      renter, renterPhone, staff, note, cond: isP ? '' : cond,
    }));
    photos.forEach((p, i) => form.append('photos', p.blob, `photo-${i + 1}.jpg`));
    form.append('sigRenter', sigA!, 'renter.png');
    form.append('sigStaff', sigB!, 'staff.png');
    setBusy(true); setError('');
    try {
      const res = await rentalApi.handover(r._id, type, form);
      const st = res.reservation.status;
      onDone(`${r.churchName} ${isP ? '수령' : '반납'} 처리를 완료했어요.${st === '확인필요' ? ' 확인 필요로 표시했어요.' : ''}${res.lateDays ? ` 반납 지연 ${res.lateDays}일을 주의 교회에 기록했어요.` : ''}`);
    } catch (e: any) { setError(e.message); } finally { setBusy(false); }
  };

  const card = { p: 2, borderRadius: 2 };
  return (
    <Box sx={{ maxWidth: 560, mx: 'auto', display: 'grid', gap: 1.5 }}>
      <Paper variant="outlined" sx={card}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'center' }}>
          <Typography fontWeight={800}>{r.no} · {r.churchName}</Typography>
          <Button size="small" variant="outlined" onClick={onBack}>목록</Button>
        </Box>
        <Typography fontSize={13} color="text.secondary">{r.contact} {r.phone} · {fmtLong(r.start)} ~ {fmtLong(r.end)} · {r.method}</Typography>
        <Typography fontSize={13} mt={0.5} fontWeight={700} color={late ? 'error.main' : 'primary.main'}>{isP ? '수령 처리' : '반납 처리'}{late ? ` · 지연 ${late}일` : ''}{!r.paid ? ' · 미입금' : ''}</Typography>
      </Paper>

      <Paper variant="outlined" sx={card}>
        <Typography fontSize={13} fontWeight={800} color="success.dark" mb={1}>{isP ? '수령' : '반납'} 수량</Typography>
        {r.lines.map((l) => (
          <Box key={l.itemId} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.75 }}>
            <Typography fontSize={14}>{l.name} <Typography component="span" fontSize={12} color="text.secondary">{isP ? '신청' : '수령'} {baseQty(l)}</Typography></Typography>
            <Stepper label={l.name} value={qty[l.itemId] || 0} base={baseQty(l)} onChange={(n) => setQty({ ...qty, [l.itemId]: n })} />
          </Box>
        ))}
      </Paper>

      {r.lines.map((l) => {
        const rows = checkRows.filter((x) => x.itemId === l.itemId);
        if (!rows.length) return null;
        return (
          <Paper key={l.itemId} variant="outlined" sx={card}>
            <Typography fontSize={13} fontWeight={800} color="success.dark" mb={1}>부속품 체크리스트 · {l.name} {qty[l.itemId] || 0}개 기준</Typography>
            {rows.map((x) => (
              <Box key={x.key} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, py: 0.75, borderBottom: '1px solid #eef0f3' }}>
                <Typography fontSize={14}>{x.c.label} {x.c.type !== 'ok' && <Typography component="span" fontSize={12} color="text.secondary">기준 {x.std}{x.c.unit}</Typography>}</Typography>
                {x.c.type === 'ok' ? (
                  <ToggleButtonGroup size="small" exclusive value={x.value} onChange={(_, v) => v && setChk({ ...chk, [x.key]: v })}>
                    <ToggleButton value="정상">정상</ToggleButton><ToggleButton value="이상" sx={{ '&.Mui-selected': { color: '#dc2626' } }}>이상</ToggleButton>
                  </ToggleButtonGroup>
                ) : <Stepper label={x.c.label} value={Number(x.value)} base={x.std} onChange={(n) => setChk({ ...chk, [x.key]: n })} />}
              </Box>
            ))}
          </Paper>
        );
      })}

      {!isP && (
        <Paper variant="outlined" sx={card}>
          <Typography fontSize={13} fontWeight={800} color="success.dark" mb={1}>상태</Typography>
          <ToggleButtonGroup size="small" exclusive value={cond} onChange={(_, v) => v && setCond(v)}>
            {['정상', '파손', '분실'].map((v) => <ToggleButton key={v} value={v}>{v}</ToggleButton>)}
          </ToggleButtonGroup>
        </Paper>
      )}
      {needNote && (
        <Paper variant="outlined" sx={{ ...card, borderColor: '#fde68a', bgcolor: '#fffbeb' }}>
          <Typography fontSize={13} fontWeight={800} color="#92400e" mb={1}>메모 (필수)</Typography>
          <TextField fullWidth multiline minRows={2} size="small" value={note} onChange={(e) => setNote(e.target.value)} placeholder="예: 버튼 1개 부족, 본부 보관 중 분실" sx={{ bgcolor: '#fff' }} />
        </Paper>
      )}

      <Paper variant="outlined" sx={card}>
        <Typography fontSize={13} fontWeight={800} color="success.dark" mb={1}>사진 (필수 · 최소 1장, 최대 3장)</Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {photos.map((p, i) => <Box key={p.url} component="img" src={p.url} alt={`사진 ${i + 1}`} sx={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 1.5, border: '1px solid #e2e8f0' }} />)}
          {photos.length < 3 && (
            <Box component="label" sx={{ width: 80, height: 80, border: '1.5px dashed #94a3b8', borderRadius: 1.5, display: 'grid', placeItems: 'center', fontSize: 12, color: 'text.secondary', cursor: 'pointer', textAlign: 'center' }}>
              📷<br />촬영·선택
              <input hidden type="file" accept="image/*" capture="environment" onChange={(e) => { addPhoto(e.target.files?.[0]); e.target.value = ''; }} />
            </Box>
          )}
        </Box>
        {photos.length > 0 && <Button size="small" color="error" sx={{ mt: 1 }} onClick={() => setPhotos([])}>사진 다시 찍기</Button>}
      </Paper>

      <Paper variant="outlined" sx={{ ...card, display: 'grid', gap: 1.5 }}>
        <Typography fontSize={13} fontWeight={800} color="success.dark">{isP ? '대여자' : '반납자'} 확인</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 1.5 }}>
          <TextField size="small" label={`${isP ? '대여자' : '반납자'} 이름`} value={renter} onChange={(e) => setRenter(e.target.value)} />
          <TextField size="small" label="연락처" value={renterPhone} onChange={(e) => setRenterPhone(e.target.value)} />
        </Box>
        <SignaturePad label={`${isP ? '대여자' : '반납자'} 서명`} onChange={setSigA} />
        <TextField select size="small" label="본부 담당자" value={staff} onChange={(e) => setStaff(e.target.value)}>
          {(settings.staff?.length ? settings.staff : ['본부 담당자']).map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
        </TextField>
        <SignaturePad label="본부 담당자 서명" onChange={setSigB} />
      </Paper>
      {error && <Alert severity="error">{error}</Alert>}
      <Button size="large" variant="contained" sx={{ minHeight: 52 }} disabled={busy} onClick={save}>{busy ? '저장 중…' : isP ? '수령 완료' : '반납 완료'}</Button>
    </Box>
  );
};

export default HandoverForm;
